import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import cx from 'classnames';

import { AutocompleteField } from '@ui/components/ui/autocomplete';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { useIsMobile } from '@ui/hooks/use-mobile';

import { cardHeaderClass, cardHeaderTitleClass, cardShellClass, tableFrameClass } from '@app/constants/styles';
import { asAmount, formatEur, newLineId, sumCosts, type BudgetSection } from './budget';
import { isFolded, readFolds, rememberFolds, setFold } from './chapterFolds';
import { useReportUnsaved } from './unsavedWork';
import { QUARTERS, type Quarter, type QuarterLine, sumQuarter } from './quarterCosts';
import { type CopyUnit, rangeContainer, rangeOverlay, useCellRange } from './cellRange';
import CellMenu from './CellMenu';
import SprintScrollRail from './SprintScrollRail';
import {
  actualTint,
  actualTintStrong,
  costColumns,
  gridHeadTint,
  hoursCell,
  hoursCellFlush,
  hoursFoldBox,
  hoursFoldOpen,
  hoursFoldShut,
  hoursHeadCell,
  hoursNameCell,
  hoursNameFlush,
  hoursRow,
  hoursTotalName,
} from './sprintGrid';

const dash = '—';
const same = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** One section of the card: what it is called, what it offers, and what is filled in against it. */
export interface OutOfPocketSection {
  key: BudgetSection;
  label: string;
  presets: readonly string[];
  lines: QuarterLine[];
}

interface DraftRow {
  id: string;
  name: string;
  amounts: Record<Quarter, string>;
}

type Draft = Record<string, DraftRow[]>;

const blank = (): Record<Quarter, string> => ({ q1: '', q2: '', q3: '', q4: '' });

/** One card with foldable sections: a section nobody fills in is a line rather than a card of dashes. */
const OutOfPocketCard = ({
  productId,
  title,
  hint,
  sections,
  onSave,
  scope,
  spent = false,
}: {
  productId: string;
  title: string;
  hint: string;
  sections: OutOfPocketSection[];
  /** Read-only without it, as every other card on the page is for whoever cannot edit the product. */
  onSave?: (lines: Record<string, QuarterLine[]>) => void;
  /** What the figures belong to, so a selected block goes when the year or the tab does. */
  scope: string;
  /** Money that went out, so its quarters are washed as the workforce grid washes a closed sprint. */
  spent?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const tintOf = (strong = false): string | false => spent && (strong ? actualTintStrong : actualTint);
  const canEdit = !!onSave;
  /** The list as typed, or nothing where nobody has typed yet: then it is the list as stored. */
  const [draft, setDraft] = useState<Draft | null>(null);
  // A figure until it is asked for, as on the workforce grid.
  const [active, setActive] = useState<{ key: string; index: number; field: Quarter | 'name' } | null>(null);
  /** Read before the first paint: from an effect, every arrival replays a collapse nobody asked for. */
  const [folded, setFolded] = useState(() => readFolds(productId, 'outOfPocket'));

  // Folds belong to the product, so switching to another one reads its own.
  useEffect(() => setFolded(readFolds(productId, 'outOfPocket')), [productId]);

  // The typing belongs to the year it was done in, and the grid above resets on the same key.
  useEffect(() => {
    setDraft(null);
    setActive(null);
  }, [productId, scope]);

  const columns = { gridTemplateColumns: costColumns };

  /** The rows and the column that are not a line or a quarter, addressed the way the grids above are. */
  const HEAD_ROW = '__head__';
  const TOTAL_ROW = '__total__';
  const NAME_COL = '__name__';
  const YEAR_COL = '__year__';
  const sectionRow = (key: string): string => `__section__:${key}`;
  const addRow = (key: string): string => `__add__:${key}`;

  const fold = (key: string, shut: boolean): void =>
    setFolded(current => {
      const next = setFold(current, key, shut);
      rememberFolds(productId, next, 'outOfPocket');
      return next;
    });

  /** The presets are not rows: that was twenty-five dashes for the three bills a product has. */
  const storedRows = (section: OutOfPocketSection): DraftRow[] =>
    section.lines.map(line => ({
      id: line.id,
      name: line.name,
      amounts: QUARTERS.reduce(
        (typed, quarter) => ({
          ...typed,
          [quarter]: line.amounts[quarter] === undefined ? '' : String(line.amounts[quarter]),
        }),
        blank(),
      ),
    }));

  const isActive = (key: string, index: number, field: Quarter | 'name'): boolean =>
    active?.key === key && active?.index === index && active?.field === field;
  /** The blur settles late, so closing whatever is active would shut the cell just asked for. */
  const stopNaming = (key: string, index: number): void =>
    setActive(current =>
      current?.key === key && current.index === index && current.field === 'name' ? null : current,
    );

  /** A section's rows as they stand: as typed, or as stored where nobody has typed yet. */
  const rowsOf = (key: string): DraftRow[] =>
    draft?.[key] ?? storedRows(sections.find(section => section.key === key) as OutOfPocketSection);
  /** The names the section offers a new line, minus the ones already on it. */
  const unusedPresets = (section: OutOfPocketSection): string[] =>
    section.presets.filter(preset => !rowsOf(section.key).some(row => same(row.name, preset)));

  /** Every section's rows as they stand, so typing into one does not drop what is in another. */
  const allRows = (): Draft => Object.fromEntries(sections.map(section => [section.key, rowsOf(section.key)]));

  // A blank quarter is simply a quarter this line does not run in.
  const badAmount = (row: DraftRow, quarter: Quarter): boolean =>
    row.amounts[quarter].trim() !== '' && asAmount(row.amounts[quarter]) === null;
  const filled = (row: DraftRow): boolean => QUARTERS.some(quarter => row.amounts[quarter].trim() !== '');
  const badRow = (row: DraftRow): boolean =>
    QUARTERS.some(quarter => badAmount(row, quarter)) || (filled(row) && row.name.trim() === '');
  const anyInvalid = sections.some(section => rowsOf(section.key).some(badRow));

  /** A name with no figures is still a line; a figure with no name is nothing to list it under. */
  const resolve = (rows: DraftRow[]): QuarterLine[] =>
    rows.flatMap(row => {
      const amounts: QuarterLine['amounts'] = {};
      QUARTERS.forEach(quarter => {
        const amount = row.amounts[quarter].trim() === '' ? null : asAmount(row.amounts[quarter]);
        if (amount !== null) amounts[quarter] = amount;
      });
      return row.name.trim() === '' ? [] : [{ id: row.id, name: row.name.trim(), amounts }];
    });
  const resolved: Record<string, QuarterLine[]> = Object.fromEntries(
    sections.map(section => [section.key, resolve(rowsOf(section.key))]),
  );

  // Dirty against what is stored: a row added and taken away again changes nothing.
  const asStored = (lines: Record<string, QuarterLine[]>): string =>
    JSON.stringify(
      // The name as typed, so correcting "gsuite" to "GSuite" is a change like any other.
      sections.map(section => (lines[section.key] ?? []).map(line => [line.name, line.amounts]).sort()),
    );
  const stored = Object.fromEntries(sections.map(section => [section.key, section.lines]));
  // Typing the card refuses resolves to nothing, and without this it goes silent with typing on screen.
  const dirty = asStored(resolved) !== asStored(stored) || anyInvalid;
  const canSave = dirty && !anyInvalid;
  // Whatever would take the reader off this year or this tab asks before it costs them this typing.
  useReportUnsaved('outOfPocket', dirty);

  const change = (key: string, at: number, change_: (row: DraftRow) => DraftRow): void =>
    setDraft({ ...allRows(), [key]: rowsOf(key).map((row, index) => (index === at ? change_(row) : row)) });
  const add = (key: string): void => {
    const rows = rowsOf(key);
    setDraft({ ...allRows(), [key]: [...rows, { id: newLineId(), name: '', amounts: blank() }] });
    // Straight into the name, which is the one thing a new line has nothing to say without.
    setActive({ key, index: rows.length, field: 'name' });
  };
  const remove = (key: string, at: number): void =>
    setDraft({ ...allRows(), [key]: rowsOf(key).filter((_, index) => index !== at) });

  /** What a section holds as it stands: the rows as typed, resolved to lines. */
  const linesOf = (section: OutOfPocketSection): QuarterLine[] => resolved[section.key];
  const quarterOf = (section: OutOfPocketSection, quarter: Quarter): number => sumQuarter(linesOf(section), quarter);
  const yearOf = (section: OutOfPocketSection): number =>
    sumCosts(QUARTERS.map(quarter => quarterOf(section, quarter)));
  const cardQuarter = (quarter: Quarter): number => sumCosts(sections.map(section => quarterOf(section, quarter)));
  const cardYear = (): number => sumCosts(sections.map(yearOf));
  const money = (amount: number): string => (amount === 0 ? dash : formatEur(amount));
  /** The blank is what says the cell is a field, as on the workforce grid above; a zero is a figure. */
  const typedMoney = (amount: number | null): string => (amount === null ? '' : formatEur(amount));

  /** Which line a row id belongs to, since a block reaches across sections as easily as down one. */
  const lineAt = (rowId: string): { key: string; index: number } | null =>
    sections
      .map(section => ({ key: section.key, index: rowsOf(section.key).findIndex(row => row.id === rowId) }))
      .find(found => found.index !== -1) ?? null;

  /** The quarter a column addresses, or nothing where the column is the name or the year. */
  const quarterAt = (col: string): Quarter | null =>
    (QUARTERS as readonly string[]).includes(col) ? (col as Quarter) : null;

  /** The quarters of a section are as much a block as the sprints of a chapter. */
  const range = useCellRange({
    rows: [
      HEAD_ROW,
      ...sections.flatMap(section => [
        sectionRow(section.key),
        ...(isFolded(folded, section.key)
          ? []
          : [...rowsOf(section.key).map(row => row.id), ...(canEdit ? [addRow(section.key)] : [])]),
      ]),
      TOTAL_ROW,
    ],
    cols: [NAME_COL, ...QUARTERS, YEAR_COL],
    readCell: (row, col) => {
      if (row === HEAD_ROW) {
        if (col === NAME_COL) return t('budget.lineName');
        if (col === YEAR_COL) return t('budget.yearColumn');
        return t(`budget.quarters.${col}`);
      }
      if (row === TOTAL_ROW) {
        if (col === NAME_COL) return t('budget.sectionTotal');
        return money(col === YEAR_COL ? cardYear() : cardQuarter(col as Quarter));
      }
      const section = sections.find(each => sectionRow(each.key) === row);
      if (section) {
        if (col === NAME_COL) return section.label;
        return money(col === YEAR_COL ? yearOf(section) : quarterOf(section, col as Quarter));
      }
      const line = lineAt(row);
      if (!line) return '';
      const typed = rowsOf(line.key)[line.index];
      if (col === NAME_COL) return typed.name;
      // A row nobody has filled in copies as a blank, not as a zero it does not show either.
      if (col === YEAR_COL)
        return filled(typed)
          ? typedMoney(
              sumCosts(QUARTERS.map(q => (typed.amounts[q].trim() === '' ? null : asAmount(typed.amounts[q])))),
            )
          : '';
      return typed.amounts[col as Quarter];
    },
    // One update for the whole block: a cell at a time reads a stale draft and keeps only the last.
    writeCells: canEdit
      ? cells =>
          setDraft(current => {
            const next: Draft = { ...(current ?? allRows()) };
            cells.forEach(({ row, col, value }) => {
              const line = lineAt(row);
              const quarter = quarterAt(col);
              if (!line || !quarter) return;
              next[line.key] = (next[line.key] ?? []).map((each, index) =>
                index === line.index ? { ...each, amounts: { ...each.amounts, [quarter]: value } } : each,
              );
            });
            return next;
          })
      : undefined,
    // A line's own quarter, and nothing else: the names, the section lines and every total are read-only.
    isEditable: (row, col) => canEdit && !!lineAt(row) && !!quarterAt(col),
    passiveRows: sections.map(section => addRow(section.key)),
    resetOn: `${productId}:${scope}`,
    leadIsCost: true,
  });

  const discard = (): void => {
    setDraft(null);
    setActive(null);
    // Or a refused copy is still saying so under a card that has since saved.
    range.clear();
  };

  const save = (): void => {
    if (!onSave) return;
    onSave(resolved);
    discard();
  };

  /** What the cell held when it was opened, so Escape undoes this edit rather than every earlier one. */
  const openedWith = useRef('');
  const scroller = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // The cell being typed into is also where a pasted block lands, as on the workforce grid.
  const openQuarter = (rowId: string, key: string, index: number, quarter: Quarter): void => {
    if (!canEdit) return;
    range.anchorAt(rowId, quarter);
    openedWith.current = rowsOf(key)[index]?.amounts[quarter] ?? '';
    setActive({ key, index, field: quarter });
  };

  /** `hatched` is a row the block reaches across but never holds, as a spreadsheet marks one. */
  const cellRange = (row: string, col: string, options?: { hatched?: boolean }) => ({
    className: range.selected(row, col) ? rangeOverlay(range.edges(row, col), options) : undefined,
    ...range.cellHandlers(row, col),
  });

  return (
    <section className={cardShellClass}>
      <div className={cx(cardHeaderClass, 'print:hidden')}>
        <div className={cardHeaderTitleClass}>
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">{hint}</p>
        </div>
        {/* A row this tall whether or not it holds a button, or the sections shift by 4px. */}
        <div className="flex min-h-7 shrink-0 items-center gap-2">
          {/* Nothing to press until something has been typed, the way the workforce grid offers it. */}
          {canEdit && dirty && (
            <>
              <Button variant="ghost" size="sm" onClick={discard}>
                {t('budget.discard')}
              </Button>
              <Button size="sm" disabled={!canSave} onClick={save}>
                {t('actions.save')}
              </Button>
            </>
          )}
        </div>
      </div>

      <div ref={scroller} className={cx(tableFrameClass, 'no-scrollbar mb-5')}>
        {/* The roles put the table back for anything reading this rather than looking at it. */}
        <div
          role="table"
          aria-label={title}
          ref={range.containerHandlers.ref}
          tabIndex={range.containerHandlers.tabIndex}
          onCopy={active ? undefined : range.containerHandlers.onCopy}
          // A block closes the cell it landed on, so it is read as figures, not through the field.
          onPaste={event => {
            range.containerHandlers.onPaste(event);
            if (event.defaultPrevented) setActive(null);
          }}
          onKeyDown={active ? undefined : range.containerHandlers.onKeyDown}
          // As wide as its columns, or a fold box stops at the screen and drops the cells past it.
          // The names scroll with the figures on a phone: pinned, they took half the narrow screen.
          className={cx('min-w-max max-md:[&_.sticky]:static', rangeContainer)}
        >
          <div role="row" style={columns} className={hoursRow}>
            <div
              role="columnheader"
              {...cellRange(HEAD_ROW, NAME_COL)}
              className={cx(hoursNameCell, 'font-medium', cellRange(HEAD_ROW, NAME_COL).className)}
            >
              {t('budget.lineName')}
            </div>
            {QUARTERS.map(quarter => {
              const cell = cellRange(HEAD_ROW, quarter);
              return (
                <div
                  key={quarter}
                  role="columnheader"
                  {...cell}
                  className={cx(hoursHeadCell, tintOf(true) || gridHeadTint, cell.className)}
                >
                  {t(`budget.quarters.${quarter}`)}
                </div>
              );
            })}
            <div
              role="columnheader"
              {...cellRange(HEAD_ROW, YEAR_COL)}
              className={cx(hoursHeadCell, gridHeadTint, cellRange(HEAD_ROW, YEAR_COL).className)}
            >
              {t('budget.yearColumn')}
            </div>
          </div>

          {sections.map(section => {
            const shut = isFolded(folded, section.key);
            const rows = rowsOf(section.key);
            // Shut, the refused row is clipped away, so the header is the only place left to say so.
            const hidesRefused = shut && rows.some(badRow);
            return (
              <div key={section.key} role="rowgroup">
                {/* The section's own row: its name, and its total in every quarter. */}
                <div role="row" style={columns} className={hoursRow}>
                  <div
                    role="rowheader"
                    {...cellRange(sectionRow(section.key), NAME_COL)}
                    className={cx(
                      hoursNameFlush,
                      'font-semibold',
                      cellRange(sectionRow(section.key), NAME_COL).className,
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => !range.claimedClick() && fold(section.key, !shut)}
                      aria-expanded={!shut}
                      className="flex size-full cursor-pointer items-center gap-1.5 px-3 text-left font-semibold text-foreground"
                    >
                      <ChevronDown
                        className={cx(
                          'size-4 shrink-0 text-muted-foreground transition-transform duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none',
                          shut && '-rotate-90',
                        )}
                      />
                      <span className={cx('truncate', hidesRefused && 'text-destructive')} title={section.label}>
                        {section.label}
                      </span>
                    </button>
                  </div>
                  {QUARTERS.map(quarter => {
                    const cell = cellRange(sectionRow(section.key), quarter);
                    return (
                      <div
                        key={quarter}
                        role="cell"
                        {...cell}
                        className={cx(
                          hoursCell,
                          'font-semibold tabular-nums',
                          tintOf(true) || gridHeadTint,
                          cell.className,
                        )}
                      >
                        {money(quarterOf(section, quarter))}
                      </div>
                    );
                  })}
                  <div
                    role="cell"
                    {...cellRange(sectionRow(section.key), YEAR_COL)}
                    className={cx(
                      hoursCell,
                      'font-semibold tabular-nums',
                      gridHeadTint,
                      cellRange(sectionRow(section.key), YEAR_COL).className,
                    )}
                  >
                    {money(yearOf(section))}
                  </div>
                </div>

                {/* The lines, in a box the fold cuts off from the bottom; `--rows` is its open height. */}
                <div
                  role="rowgroup"
                  // The Add row is a row the box has to make height for, and only where it renders.
                  style={{ '--rows': rows.length + (canEdit ? 1 : 0) } as React.CSSProperties}
                  className={cx(hoursFoldBox, shut ? hoursFoldShut : hoursFoldOpen)}
                >
                  {rows.map((row, index) => {
                    const naming = isActive(section.key, index, 'name');
                    return (
                      // Hovering the row tints every cell it can be typed into, as on the workforce grid.
                      <div key={row.id} role="row" style={columns} className={cx(hoursRow, 'group/row')}>
                        {/* The remove control sits in the name it removes, not in a column of its own. */}
                        <div
                          role="rowheader"
                          {...(naming ? {} : cellRange(row.id, NAME_COL))}
                          className={cx(
                            hoursNameFlush,
                            'font-normal',
                            !naming && cellRange(row.id, NAME_COL).className,
                          )}
                        >
                          <div className="flex size-full items-center gap-1 pl-7 pr-1 max-md:pl-4">
                            {naming ? (
                              // Offered rather than imposed: a line takes any name typed into it.
                              <AutocompleteField
                                aria-label={t('budget.lineNameOf', { section: section.label, number: index + 1 })}
                                value={row.name}
                                placeholder={t('budget.linePlaceholder')}
                                suggestions={unusedPresets(section)}
                                // Open on arrival: the click that made the field was the click for the list.
                                defaultOpen
                                autoFocus
                                onBlur={() => stopNaming(section.key, index)}
                                onChange={next => change(section.key, index, current => ({ ...current, name: next }))}
                                className={cx(
                                  'min-w-0 flex-1 bg-popover',
                                  filled(row) && row.name.trim() === '' && 'border-destructive',
                                )}
                              />
                            ) : (
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() =>
                                  !range.claimedClick() && setActive({ key: section.key, index, field: 'name' })
                                }
                                className={cx(
                                  'min-w-0 flex-1 truncate rounded px-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                  canEdit && 'cursor-text',
                                  row.name.trim() === '' && 'text-muted-foreground',
                                  // What greys Save out, and the open field marks: the closed cell must too.
                                  filled(row) && row.name.trim() === '' && 'bg-destructive/10 text-destructive',
                                )}
                              >
                                {row.name.trim() === '' ? t('budget.linePlaceholder') : row.name}
                              </button>
                            )}
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="icon-xs"
                                aria-label={`${t('actions.remove')} ${row.name || t('budget.lineName')}`}
                                onClick={() => !range.claimedClick() && remove(section.key, index)}
                              >
                                <Trash2 className="size-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                        {QUARTERS.map(quarter => {
                          const typing = isActive(section.key, index, quarter);
                          const label = `${row.name || t('budget.lineName')}, ${t(`budget.quarters.${quarter}`)}`;
                          const amount = row.amounts[quarter];
                          const held = amount.trim() === '' ? null : asAmount(amount);
                          return (
                            <div
                              key={quarter}
                              role="cell"
                              // Focusable, and landing on it is what opens it: Tab walks the quarters.
                              tabIndex={typing || !canEdit ? undefined : 0}
                              onFocus={
                                typing || !canEdit ? undefined : () => openQuarter(row.id, section.key, index, quarter)
                              }
                              // A drag that became a selection has already used this press.
                              onClick={
                                typing || !canEdit
                                  ? undefined
                                  : () => !range.claimedClick() && openQuarter(row.id, section.key, index, quarter)
                              }
                              onKeyDown={
                                typing || !canEdit
                                  ? undefined
                                  : event => {
                                      if (event.key !== 'Enter' && event.key !== ' ') return;
                                      event.preventDefault();
                                      openQuarter(row.id, section.key, index, quarter);
                                    }
                              }
                              {...(typing
                                ? range.openCellHandlers(row.id, quarter)
                                : range.cellHandlers(row.id, quarter))}
                              className={cx(
                                typing ? hoursCellFlush : hoursCell,
                                !typing &&
                                  canEdit &&
                                  'cursor-text outline-none group-hover/row:bg-muted/25 hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                                !typing && (badAmount(row, quarter) ? 'bg-destructive/10' : tintOf()),
                                !typing &&
                                  range.selected(row.id, quarter) &&
                                  rangeOverlay(range.edges(row.id, quarter)),
                                typing &&
                                  range.selected(row.id, quarter) &&
                                  rangeOverlay(range.edges(row.id, quarter), { open: true }),
                              )}
                            >
                              {typing ? (
                                <Input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  aria-label={label}
                                  value={amount}
                                  placeholder={t('budget.amountPlaceholder')}
                                  autoFocus
                                  onBlur={() => setActive(null)}
                                  // The container's keys are off while a cell is open, so the way out is here.
                                  onKeyDown={event => {
                                    if (event.key === 'Enter') {
                                      event.preventDefault();
                                      setActive(null);
                                      return;
                                    }
                                    if (event.key !== 'Escape') return;
                                    event.preventDefault();
                                    change(section.key, index, current => ({
                                      ...current,
                                      amounts: {
                                        ...current.amounts,
                                        [quarter]: openedWith.current,
                                      },
                                    }));
                                    setActive(null);
                                  }}
                                  // A number field takes the wheel as input, and this card is scrolled past.
                                  onWheel={event => event.currentTarget.blur()}
                                  onChange={event =>
                                    change(section.key, index, current => ({
                                      ...current,
                                      amounts: { ...current.amounts, [quarter]: event.target.value },
                                    }))
                                  }
                                  className={cx(
                                    'size-full rounded-none border-0 px-3 text-right',
                                    // Inside its own cell: the kit rings 3px outwards, over the rows either side.
                                    'focus-visible:ring-2 focus-visible:ring-inset',
                                    '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                                    badAmount(row, quarter)
                                      ? 'bg-destructive/10 hover:bg-destructive/15'
                                      : 'bg-muted/20 hover:bg-muted/40',
                                  )}
                                />
                              ) : (
                                <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground max-md:text-xs">
                                  {typedMoney(held)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                        <div
                          role="cell"
                          {...cellRange(row.id, YEAR_COL)}
                          className={cx(
                            hoursCell,
                            'tabular-nums text-muted-foreground',
                            cellRange(row.id, YEAR_COL).className,
                          )}
                        >
                          {/* A row nobody has filled in yet is a dash, the same as once it is saved. */}
                          {filled(row)
                            ? formatEur(
                                QUARTERS.reduce((total, quarter) => total + (asAmount(row.amounts[quarter]) ?? 0), 0),
                              )
                            : dash}
                        </div>
                      </div>
                    );
                  })}

                  {/* A row that is only a way in to adding one, so it goes with the adding. */}
                  {canEdit && (
                    <div role="row" style={columns} className={hoursRow}>
                      <div
                        role="rowheader"
                        {...cellRange(addRow(section.key), NAME_COL, { hatched: true })}
                        className={cx(
                          hoursNameFlush,
                          'font-normal',
                          cellRange(addRow(section.key), NAME_COL, { hatched: true }).className,
                        )}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-7 max-md:ml-2"
                          onClick={() => !range.claimedClick() && add(section.key)}
                        >
                          <Plus className="size-4" />
                          {t('budget.addCost')}
                        </Button>
                      </div>
                      {[...QUARTERS, YEAR_COL].map(column => (
                        <div
                          key={column}
                          role="cell"
                          {...cellRange(addRow(section.key), column, { hatched: true })}
                          className={cx(
                            hoursCell,
                            column !== YEAR_COL && tintOf(),
                            cellRange(addRow(section.key), column, { hatched: true }).className,
                          )}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* What the whole card comes to, whichever section it was filled in against. */}
          <div role="row" style={columns} className={hoursRow}>
            <div
              role="rowheader"
              {...cellRange(TOTAL_ROW, NAME_COL)}
              className={cx(hoursTotalName, 'border-b-0 font-bold', cellRange(TOTAL_ROW, NAME_COL).className)}
            >
              {t('budget.sectionTotal')}
            </div>
            {QUARTERS.map(quarter => (
              <div
                key={quarter}
                role="cell"
                {...cellRange(TOTAL_ROW, quarter)}
                className={cx(
                  hoursCell,
                  'border-b-0 font-bold tabular-nums',
                  tintOf(true) || 'bg-muted/60',
                  cellRange(TOTAL_ROW, quarter).className,
                )}
              >
                {money(cardQuarter(quarter))}
              </div>
            ))}
            <div
              role="cell"
              {...cellRange(TOTAL_ROW, YEAR_COL)}
              className={cx(
                hoursCell,
                'border-b-0 font-bold tabular-nums',
                'bg-muted/60',
                cellRange(TOTAL_ROW, YEAR_COL).className,
              )}
            >
              {money(cardYear())}
            </div>
          </div>
        </div>
        <SprintScrollRail
          scroller={scroller}
          nameWidth={isMobile ? undefined : 'var(--cost-name-width)'}
          columns={QUARTERS.length}
        />
      </div>

      {/* The clipboard can refuse a copy, and a card that says nothing looks like it did nothing. */}
      {range.copyFailed && <p className="px-5 max-md:px-4 pb-4 text-sm text-destructive">{t('cells.copyFailed')}</p>}

      {/* One figure a cell, so there is nothing to ask which of them is wanted: the menu just copies. */}
      <CellMenu
        at={range.menuAt}
        onClose={range.closeMenu}
        items={[
          { label: t('cells.copy'), onSelect: () => range.copyAs('lead' as CopyUnit) },
          ...(range.hasEditable() ? [{ label: t('cells.clear'), onSelect: () => range.clearCells() }] : []),
        ]}
      />
    </section>
  );
};

export default OutOfPocketCard;
